import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useCustomers } from "@/hooks/use-customers";
import { useCreateOffer, useOfferTemplates, type OfferTemplate } from "@/hooks/use-offers";
import { useNavigate } from "react-router-dom";
import { NewCustomerDialog } from "@/components/new-project/NewCustomerDialog";
import { SrIcon } from "@/components/ui/SrIcon";
import {
  InvoiceTermsEditor,
  DEFAULT_INVOICE_TERMS,
  type InvoiceTerm,
} from "@/components/offer/InvoiceTermsEditor";
import { OfferSizeTemplatePreview } from "@/components/offer/OfferSizeTemplatePreview";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** VIB-2096 B: mal-kode fra ⌘K (ikke hardkodet XS/S/M/L). */
  initialSize?: string | null;
  /** CRM-2.7: lag tilbud fra en prosess. Kunden er forhåndsvalgt (men kan byttes). */
  initialCustomerId?: string | null;
  initialTitle?: string;
  opportunityId?: string | null;
}

export function NewOfferDialog({ open, onOpenChange, initialSize, initialCustomerId, initialTitle, opportunityId }: Props) {
  const navigate = useNavigate();
  const { data: customers = [] } = useCustomers();
  const { data: templates = [] } = useOfferTemplates();
  const createOffer = useCreateOffer();

  const [title, setTitle] = useState("");
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerPopoverOpen, setCustomerPopoverOpen] = useState(false);
  const [size, setSize] = useState<string>("none");
  const [includeStills, setIncludeStills] = useState(false);
  const [includeReise, setIncludeReise] = useState(false);
  const [showInvoiceTerms, setShowInvoiceTerms] = useState(true);
  const [invoiceTerms, setInvoiceTerms] = useState<InvoiceTerm[]>(DEFAULT_INVOICE_TERMS);
  const [conceptRouting, setConceptRouting] = useState(false);
  // VIB-993: reise faktureres separat (medgått) — settes allerede ved opprettelse.
  const [billsTravelPerMedgatt, setBillsTravelPerMedgatt] = useState(false);

  const sizeTemplates = templates.filter((t) => t.kind === "size");
  const hasStills = templates.some((t) => t.code === "stills");
  const hasReise = templates.some((t) => t.code === "reise");

  const selectedCustomer = customers.find((c) => c.id === customerId);
  const invoiceTermsSum = invoiceTerms.reduce(
    (acc, t) => acc + (Number(t.pct) || 0),
    0,
  );
  const invoiceTermsValid =
    !showInvoiceTerms || Math.abs(invoiceTermsSum - 100) < 0.01;
  const canSubmit =
    title.trim().length > 0 && customerId !== null && invoiceTermsValid;

  useEffect(() => {
    if (!open) return;
    if (initialSize) setSize(initialSize);
  }, [open, initialSize]);

  // CRM-2.7: forhåndsutfylling fra en prosess.
  useEffect(() => {
    if (!open) return;
    if (initialCustomerId) setCustomerId(initialCustomerId);
    if (initialTitle) setTitle(initialTitle);
  }, [open, initialCustomerId, initialTitle]);

  function resolveTemplateCodes(): string[] {
    const codes: string[] = [];
    if (size !== "none") codes.push(size);
    if (includeStills && hasStills) codes.push("stills");
    if (includeReise && hasReise) codes.push("reise");
    return codes;
  }

  function resetForm() {
    setTitle("");
    setCustomerId(null);
    setSize("none");
    setIncludeStills(false);
    setIncludeReise(false);
    setShowInvoiceTerms(true);
    setInvoiceTerms(DEFAULT_INVOICE_TERMS);
    setConceptRouting(false);
    setBillsTravelPerMedgatt(false);
  }

  async function handleCreate() {
    if (!canSubmit) return;
    try {
      // Avgjør om vi sender invoice_terms: bare hvis brukeren har avveket
      // fra defaultet (60/40, label="Oppstart"/"Levering"). Sparer en
      // skriving til DB og holder pre-migrasjon-kompat.
      const termsChanged =
        invoiceTerms.length !== DEFAULT_INVOICE_TERMS.length ||
        invoiceTerms.some(
          (t, i) =>
            t.label !== DEFAULT_INVOICE_TERMS[i].label ||
            t.pct !== DEFAULT_INVOICE_TERMS[i].pct,
        );

      const offer = await createOffer.mutateAsync({
        title: title.trim(),
        customer_id: customerId,
        // Cache kundenavn på tilbudet for historisk visning
        customer_name: selectedCustomer?.name ?? null,
        template_codes: resolveTemplateCodes(),
        // Send kun ved avvik fra default (true) — gjør koden bakover-
        // kompatibel før migrasjonen er kjørt mot prod-DB.
        ...(showInvoiceTerms ? {} : { show_invoice_terms: false }),
        ...(termsChanged ? { invoice_terms: invoiceTerms } : {}),
        ...(conceptRouting ? { concept_routing: true } : {}),
        ...(billsTravelPerMedgatt ? { bills_travel_per_medgatt: true } : {}),
        // Bytter brukeren kunde, hører tilbudet ikke lenger til prosessen.
        ...(opportunityId && customerId === initialCustomerId ? { opportunity_id: opportunityId } : {}),
      });
      toast.success("Tilbud opprettet");
      onOpenChange(false);
      resetForm();
      navigate(`/tilbud/${offer.id}`);
    } catch (err) {
      toast.error(`Kunne ikke opprette tilbud: ${(err as Error).message}`);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nytt tilbud</DialogTitle>
        </DialogHeader>
        {/* <form> så Enter i tittelfeltet oppretter når skjemaet er gyldig (VIB-2543).
            Select/Popover-triggere er type="button" og submitter ikke. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleCreate();
          }}
        >
        <div className="space-y-4">
          <div>
            <Label htmlFor="offer-title">Tittel</Label>
            <Input
              id="offer-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="F.eks. Europris bursdagsfilm"
            />
          </div>

          <div>
            <Label>
              Kunde <span className="text-destructive">*</span>
            </Label>
            <div className="flex gap-2">
              <Popover open={customerPopoverOpen} onOpenChange={setCustomerPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button type="button"
                    variant="outline"
                    role="combobox"
                    className="flex-1 justify-between font-normal"
                  >
                    {selectedCustomer?.name ?? "Velg kunde…"}
                    <SrIcon name="chevrons-up-down" className="ml-2 h-4 w-4 shrink-0 opacity-50"  />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Søk kunder…" />
                    <CommandList>
                      <CommandEmpty>Ingen treff</CommandEmpty>
                      <CommandGroup>
                        {customers.map((c) => (
                          <CommandItem
                            key={c.id}
                            value={c.name}
                            onSelect={() => {
                              setCustomerId(c.id);
                              setCustomerPopoverOpen(false);
                            }}
                          >
                            <SrIcon name="check"
                              className={cn(
                                "mr-2 h-4 w-4",
                                customerId === c.id ? "opacity-100" : "opacity-0",
                              )}
                             />
                            {c.name}
                            {c.country_code && c.country_code !== "NO" && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                {c.country_code}
                              </span>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <NewCustomerDialog onCustomerCreated={(id) => setCustomerId(id)} />
            </div>
          </div>

          {/* VIB-553: Malvelger */}
          {sizeTemplates.length > 0 && (
            <div className="space-y-3 rounded-md border bg-muted/30 p-3">
              <div>
                <Label className="text-sm font-medium">Størrelse</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Forhåndsfyller tilbudet med standard linjer. Kan justeres etterpå.
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { value: "none", label: "Ingen", template: null as OfferTemplate | null },
                    ...sizeTemplates.map((t) => ({
                      value: t.code,
                      label: t.name,
                      template: t,
                    })),
                  ].map((opt) => {
                    const btn = (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => setSize(opt.value)}
                        className={cn(
                          "rounded-md border bg-background px-2 py-1.5 text-sm transition-colors hover:bg-muted/50",
                          size === opt.value && "border-primary ring-1 ring-primary bg-primary/5 font-medium",
                        )}
                      >
                        {opt.label}
                      </button>
                    );
                    if (!opt.template) return btn;
                    return (
                      <HoverCard key={opt.value} openDelay={250} closeDelay={100}>
                        <HoverCardTrigger asChild>{btn}</HoverCardTrigger>
                        <HoverCardContent side="top" align="center" className="w-72">
                          <OfferSizeTemplatePreview template={opt.template} />
                        </HoverCardContent>
                      </HoverCard>
                    );
                  })}
                </div>
              </div>

              {(hasStills || hasReise) && (
                <div>
                  <Label className="text-sm font-medium">Tillegg</Label>
                  <div className="mt-2 flex gap-4">
                    {hasStills && (
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={includeStills}
                          onChange={(e) => setIncludeStills(e.target.checked)}
                          className="h-4 w-4 rounded border-input accent-primary"
                        />
                        Stills
                      </label>
                    )}
                    {hasReise && (
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={includeReise}
                          onChange={(e) => setIncludeReise(e.target.checked)}
                          className="h-4 w-4 rounded border-input accent-primary"
                        />
                        Reise
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* VIB-663a: Konsept-routing toggle. Ruter den auto-genererte
              tilbudsskriving-linja til Konseptutvikling-malen i stedet
              for prosjektledelse — eneste forskjell fra et vanlig tilbud. */}
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={conceptRouting}
              onChange={(e) => setConceptRouting(e.target.checked)}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            <span className="font-medium">Dette er en konseptutvikling</span>
          </label>

          <div className="space-y-3">
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={showInvoiceTerms}
                onChange={(e) => setShowInvoiceTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
              />
              <span>
                <span className="font-medium">Inkluder faktureringsvilkår</span>
                <span className="block text-xs text-muted-foreground">
                  Vises som fotnote i kopier-bilde og Keynote. Kan endres senere.
                </span>
              </span>
            </label>

            {showInvoiceTerms && (
              <InvoiceTermsEditor terms={invoiceTerms} onChange={setInvoiceTerms} />
            )}

            {/* VIB-993: reise faktureres separat (medgått). */}
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={billsTravelPerMedgatt}
                onChange={(e) => setBillsTravelPerMedgatt(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
              />
              <span>
                <span className="font-medium">Reise faktureres separat (medgått)</span>
                <span className="block text-xs text-muted-foreground">
                  Tas med som faktureringsvilkår og overføres til prosjektet ved konvertering.
                </span>
              </span>
            </label>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button type="submit" disabled={!canSubmit || createOffer.isPending}>
            {createOffer.isPending && <SrIcon name="loader2" className="mr-2 h-4 w-4 animate-spin"  />}
            Opprett
          </Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
